import React, { useRef, useCallback } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import { useGoogleMapsLoaded } from './GoogleMapsProvider';
import { useSettings } from '../contexts/SettingsContext';
import { CountryCode } from '../types';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Map CountryCode to Google Maps country restriction codes
// Most are the same, but some need adjustment
const getCountryRestriction = (country: CountryCode): string => {
  const countryMap: Record<CountryCode, string> = {
    fi: 'fi',
    se: 'se',
    no: 'no',
    dk: 'dk',
    pl: 'pl',
    nl: 'nl',
    de: 'de',
    at: 'at',
    be: 'be',
    fr: 'fr',
    gb: 'gb',
    tr: 'tr',
  };
  return countryMap[country] || 'fi';
};

/**
 * Address input with Google Places Autocomplete
 * Falls back to plain text input when Google Maps is not loaded
 */
export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  placeholder,
  className,
  disabled
}) => {
  const { isLoaded } = useGoogleMapsLoaded();
  const { settings } = useSettings();
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.formatted_address) {
        onChange(place.formatted_address);
      } else if (place.name) {
        // Fallback to name if formatted_address is not available
        onChange(place.name);
      }
    }
  }, [onChange]);

  // If Google Maps not loaded, render plain input
  if (!isLoaded) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />
    );
  }

  // Get country code for restrictions
  const countryCode = getCountryRestriction(settings.companyCountry);

  return (
    <Autocomplete
      onLoad={onLoad}
      onPlaceChanged={onPlaceChanged}
      restrictions={{ country: countryCode }}
      types={['address']}
      options={{
        fields: ['formatted_address', 'name', 'address_components'],
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />
    </Autocomplete>
  );
};

export default AddressAutocomplete;
